import { Point } from 'pixi.js';

import { getScreenSize } from './core/screen';
import {
    DESKTOP_SIDEBAR_WIDTH,
    DESKTOP_UI_WIDTH,
    DESKTOP_WIDTH_BREAK_POINT,
    Displays,
    TABLET_WIDTH_BREAK_POINT,
} from 'constants/layout';
import { EventName } from './events/eventsName';
import { GameEvent } from './events/gameEvent';
import { Store } from './store/gameStore';
import { GameName } from 'constants/difficulty';
import { lerp } from './core/lerp';
import { BaseGameView, BaseStrategy } from './strategies/BaseStrategy';

class LayoutManager {
    settingAreOpen = false;
    columns = 7;
    freeCells = 0;
    foundations = 4;
    stockPile = 0;
    westPile = 0;

    constructor() {
        GameEvent.add(EventName.OPEN_SETTINGS, () => {
            this.settingAreOpen = true;
        });
        GameEvent.add(EventName.HIDE_SETTINGS, () => {
            this.settingAreOpen = false;
        });
    }

    setLayout() {
        switch (Store.name) {
            case GameName.KLONDIKE:
                this.columns = 7;
                this.foundations = 4;
                this.freeCells = 0;
                this.stockPile = 1;
                this.westPile = 1;
                break;
            case GameName.SPIDER:
                this.columns = 10;
                this.foundations = 8;
                this.freeCells = 0;
                this.stockPile = 1;
                this.westPile = 0;
                break;
            case GameName.FREECELL:
                this.columns = 8;
                this.foundations = 4;
                this.freeCells = 4;
                this.stockPile = 0;
                this.westPile = 0;
                break;
            case GameName.PYRAMID:
                this.columns = 7;
                this.foundations = 1;
                this.freeCells = 0;
                this.stockPile = 1;
                this.westPile = 0;
                break;
        }
    }

    get IsHorizontalOrientationMobile() {
        const { width, height } = getScreenSize();
        return this.getDisplay() === Displays.MOBILE && height < width;
    }

    get IsVerticalOrientationMobile() {
        const { width, height } = getScreenSize();
        return this.getDisplay() === Displays.MOBILE && height > width;
    }

    getDisplay() {
        const { width, height } = getScreenSize();
        if (height < 600) return Displays.MOBILE;
        if (width < TABLET_WIDTH_BREAK_POINT) return Displays.MOBILE;
        else if (width < DESKTOP_WIDTH_BREAK_POINT) return Displays.TABLET;
        else return Displays.DESKTOP;
    }
    getColumnPosition(index: number): Point {
        const columnsGap = this.getColumnsGap() + this.getCardSize().width;
        const startPosition = this.getColumnsStartPosition();

        return new Point(startPosition.x + index * columnsGap, startPosition.y);
    }

    getColumnsAdditionalGap() {
        if (Store.name === GameName.FREECELL && this.getDisplay() !== Displays.MOBILE) {
            return 5;
        }

        return 0;
    }

    getColumnsGap() {
        const cardSize = this.getCardSize();
        const { width } = this.getTableSize();

        const freeSpace = width - cardSize.width * this.columns;
        const gap = freeSpace / (this.columns - 1);
        return gap + this.getColumnsAdditionalGap();
    }

    getTableSize() {
        const screenSize = getScreenSize();
        let width = screenSize.width;
        let height = screenSize.height;
        const ratioPrc = height / width;
        const ratioBreakPoint = 0.8;
        if (ratioPrc < ratioBreakPoint) {
            width = lerp(width * 0.15, width, ratioPrc / ratioBreakPoint);
        }
        let tableWidth = width;
        const strategy = BaseStrategy.instance;
        const strategyView = BaseGameView.instance;
        const tableSize = strategyView?.getTableSize() || 0;

        switch (this.getDisplay()) {
            case Displays.MOBILE:
                const gap = strategy ? tableSize : 8;
                tableWidth = width - gap;
                break;
            case Displays.TABLET:
                const additionalGap = Store.name === GameName.FREECELL ? 80 : 50;
                tableWidth = strategy ? width - tableSize : width - additionalGap
                break;
            case Displays.DESKTOP:
                tableWidth -= DESKTOP_SIDEBAR_WIDTH + this.tablePaddingByGame();
                const strategyGap = strategy ? tableSize : 0;
                tableWidth = !strategy ? Math.min(tableWidth, this.getColumnWidthByGame() * this.columns) : tableWidth - strategyGap;
        }

        const tableHeight = Math.max(1, height - 150); //get position of the undo button
        const bottomPanelSize = width >= 430 ? 171 : 155;
        const mobileAdditionalReducedWidth = ((this.getDisplay() === Displays.MOBILE || this.getDisplay() === Displays.TABLET) && Layout.IsHorizontalOrientationMobile) ? (strategy ? 40 : bottomPanelSize) : 10;

        return { width: tableWidth - mobileAdditionalReducedWidth, height: tableHeight };
    }
    getColumnWidthByGame() {
        switch (Store.name) {
            case GameName.KLONDIKE:
                return 115;
            case GameName.SPIDER:
                return 130;
            case GameName.FREECELL:
                return 120;
            case GameName.PYRAMID:
                return 125;
        }
    }
    tablePaddingByGame() {
        switch (Store.name) {
            case GameName.KLONDIKE:
                return 70;
            case GameName.SPIDER:
                return 20;
            case GameName.FREECELL:
                return 50;
            case GameName.PYRAMID:
                return 0;
        }
    }
    getColumnsTotalWidth() {
        const cardSize = this.getCardSize();
        const gap = this.getColumnsGap();
        return cardSize.width * this.columns + gap * (this.columns - 1);
    }

    getColumnsStartPosition() {
        const { width } = getScreenSize();
        const display = this.getDisplay();
        const top = this.getColumnRowPosition();
        const columnsWidth = Layout.getColumnsTotalWidth();
        const startX = -columnsWidth / 2 + Layout.getCardSize().width / 2;
        switch (display) {
            case Displays.MOBILE:
            case Displays.TABLET:
                return new Point(startX, top);

            case Displays.DESKTOP:
                const minPosition = -width / 2 + Layout.getCardSize().width / 2 + 10;
                let startDesktopX = startX - (this.settingAreOpen ? DESKTOP_UI_WIDTH : DESKTOP_SIDEBAR_WIDTH) / 2;
                if (startDesktopX < minPosition) startDesktopX = minPosition;
                return new Point(startDesktopX, top);
        }
    }
    getWastePilePosition(index = 0) {
        const display = this.getDisplay();
        const { width } = this.getCardSize();
        const top = this.getTopCardRowPosition();
        let gap = 0;

        const leftHanded = Store.leftHanded;
        const anchorIndex = leftHanded ? 1 : this.columns - 2;

        switch (display) {
            case Displays.MOBILE:
                gap = width * 0.48;
                break;
            case Displays.TABLET:
                gap = width * 0.45;
                break;
            case Displays.DESKTOP:
                gap = width * 0.45;
                break;
        }

        const cardGap = Store.leftHanded ? index * gap : -gap * 2 + index * gap;

        if (BaseStrategy.instance) {
            return BaseStrategy.instance.getWastePalePosition(cardGap);
        }

        return new Point(this.getColumnPosition(anchorIndex).x + cardGap, top);
    }

    getStockPilePosition(order?: number) {
        const leftHanded = Store.leftHanded;
        const top = this.getTopCardRowPosition();

        const { height } = getScreenSize();
        const cardSize = this.getCardSize();


        switch (Store.name) {
            case GameName.KLONDIKE:
                const ik = leftHanded ? 0 : this.columns - 1;
                return new Point(this.getColumnPosition(ik).x, top);
            case GameName.SPIDER:
                if (!order && order !== 0) order = 49;
                const translationIndex = Math.floor((50 - (order + 1)) / 10);
                const is = leftHanded ? 0 : this.columns - 1;
                const cardTranslation = translationIndex * this.getCardSize().width * 0.14 * (leftHanded ? 1 : -1);
                return new Point(this.getColumnPosition(is).x + cardTranslation, top);
            case GameName.FREECELL:
                return new Point(0, height / 2 + cardSize.height);
            case GameName.PYRAMID:
                return new Point(leftHanded ? this.getColumnPosition(2).x : this.getColumnPosition(4).x, this.getPyramidTopPadding() + (cardSize.height / 2) * 7.3);
        }
    }

    getFoundationPosition(index: number) {
        const top = this.getTopCardRowPosition();
        const gap = this.getColumnsGap();
        const foundationGap =
            this.getDisplay() !== Displays.MOBILE && Store.name == GameName.FREECELL ? 0 - gap + gap * 1.5 : 0;

        if (BaseStrategy.instance) {
            return BaseStrategy.instance.getFoundationPosition();
        }

        const leftHanded = Store.leftHanded;
        if (leftHanded) return new Point((foundationGap * 3) + this.getColumnPosition(this.columns - (this.foundations - index)).x - foundationGap * index, top);
        else return new Point(this.getColumnPosition(index).x - foundationGap * index, top);
    }

    getFreeCellPosition(index: number) {
        const top = this.getTopCardRowPosition();
        const gap = this.getColumnsGap();
        const foundationGap =
            this.getDisplay() !== Displays.MOBILE && Store.name == GameName.FREECELL ? 0 - gap + gap * 1.5 : 0;

        const leftHanded = Store.leftHanded;
        if (leftHanded) return new Point(this.getColumnPosition(index).x - foundationGap * index, top);
        else return new Point((foundationGap * 3) + this.getColumnPosition(this.columns - (this.foundations - index)).x - foundationGap * index, top);
    }

    getTopCardRowPosition() {
        const { height, width } = getScreenSize();
        const display = this.getDisplay();
        const cardSize = this.getCardSize();
        const screenTop = -height / 2;

        const desktopPadding = Store.tutorialActive && width <= 1366 ? 10 : 24;
        const tabletPadding = Store.tutorialActive ? 30 : 41;
        const mobilePadding = Store.tutorialActive && width > height && Store.isFreecell ? 8 : 32;

        switch (display) {
            case Displays.MOBILE:
                return screenTop + mobilePadding + cardSize.height / 2;
            case Displays.TABLET:
                return screenTop + tabletPadding + cardSize.height / 2;
            case Displays.DESKTOP:
                return screenTop + desktopPadding + cardSize.height / 2;
        }
    }


    getPyramidTopPadding() {
        const { height, width } = getScreenSize();
        const display = this.getDisplay();
        const screenTop = -height / 2;

        if (this.IsVerticalOrientationMobile && width <= 600) {
            return -140;
        }
        if (this.IsVerticalOrientationMobile && width <= 780) {
            return -230;
        } else if (width >= height * 2 && height <= 365) {

            // 365 / H
            // 1.5 / x

            const x = (height * 1.5) / 365;
            const diff = 1 - x;
            return screenTop + this.getCardSize().height * 1.5 + (diff < 0 ? 0 : diff * 1.2);
        }
        else if (display === Displays.TABLET) {
            return screenTop + this.getCardSize().height * 0.9;
        } else return screenTop + this.getCardSize().height * 0.6;
    }

    getColumnRowPosition() {
        const cardSize = this.getCardSize();
        const top = this.getTopCardRowPosition();
        const display = this.getDisplay();
        switch (display) {
            case Displays.MOBILE:
                return top + cardSize.height * 1.14;
            case Displays.TABLET:
                return top + cardSize.height * 1.1;
            case Displays.DESKTOP:
                return top + cardSize.height * 1.1;
        }
    }

    getPyramidXOffset() {
        const display = this.getDisplay();
        switch (display) {
            case Displays.MOBILE:
                return 0;
            case Displays.TABLET:
                return 0;
            case Displays.DESKTOP:
                return -60;
        }
    }

    getCardSize() {
        const display = this.getDisplay();
        let cardWidth = 10;
        const { width } = this.getTableSize();

        switch (display) {
            case Displays.MOBILE:
            case Displays.TABLET:
                cardWidth = (width * 0.96) / this.columns;
                break;
            case Displays.DESKTOP:
                cardWidth = (width * 0.97) / this.columns;
                break;
        }
        const cardRatio = 119 / 84;
        const cardHeigh = cardWidth * cardRatio;

        return { width: cardWidth, height: cardHeigh };
    }

    getShadowSize() {
        const cardSize = this.getCardSize();
        const ratioW = (318 + 30) / 318;
        const ratioH = (453 + 30) / 453;
        return { width: cardSize.width * ratioW, height: cardSize.height * ratioH };
    }

    getCardGap() {
        const { height } = this.getCardSize();
        return { normal: height * 0.38, hidden: height * 0.085 };
    }

    getMaxColumnHeight() {
        const { width, height } = getScreenSize();
        const cardSize = this.getCardSize();

        const display = this.getDisplay();
        const top = height / 2 + this.getColumnRowPosition();
        let columnSpace = height - top + cardSize.height / 2;
        //get position of the undo button
        switch (display) {
            case Displays.MOBILE:
                if (Store.tutorialActive) columnSpace -= 60;
                return width > height ? columnSpace - 80 : columnSpace - 150;
            case Displays.TABLET:
                return columnSpace - 150;
            case Displays.DESKTOP:
                if (Store.tutorialActive) columnSpace -= Store.isFreecell ? 100 : 50;
                return columnSpace - 100;
        }
    }

    getParticleScale() {
        const display = this.getDisplay();

        switch (display) {
            case Displays.MOBILE:
                return 0.25;
            case Displays.TABLET:
                return 0.45;
            case Displays.DESKTOP:
                return 0.45;
        }
    }

    getButtonPadding() {
        const display = this.getDisplay();
        switch (display) {
            case Displays.MOBILE:
                return new Point(12, 12);
            case Displays.TABLET:
                return new Point(8, 8);
            case Displays.DESKTOP:
                return new Point(12, 12);
        }
    }
    setColumns(columns: number) {
        this.columns = columns;
    }
    setFoundation(foundations: number) {
        this.foundations = foundations;
    }

    getButtonSize() {
        switch (this.getDisplay()) {
            case Displays.MOBILE:
                return 52;
            case Displays.TABLET:
            case Displays.DESKTOP:
                return 58;
        }
    }
    getBoardCenter() {
        switch (this.getDisplay()) {
            case Displays.MOBILE:
            case Displays.TABLET:
                return new Point(0, 0);

            case Displays.DESKTOP:
                return new Point(-DESKTOP_SIDEBAR_WIDTH / 2, 0);
        }
    }
    cardsInColumn(x: number) {
        switch (Store.name) {
            case GameName.KLONDIKE:
                return x + 1;
            case GameName.SPIDER:
                return x < 4 ? 6 : 5;
            case GameName.FREECELL:
                return x < 4 ? 7 : 6;
            case GameName.PYRAMID:
                return x + 1;
        }
    }
}

export const Layout = new LayoutManager();
